import { siloAbi } from "@silo/contract-abi-types";
import {
  ChainPublicClientService,
  getChainKeyFromProtocol,
  MarketMeta,
  StaticMarket,
} from "@silo/types";
import { Array, Effect, Option, pipe } from "effect";
import { getContract } from "viem";
import { ContractsClientError } from "./types";
import { AssetInfo } from "./get-market-asset-meta/types";
import { getMarketAssetMeta } from "./get-market-asset-meta";

export type GetMarketMeta = ReturnType<typeof getMarketMeta>;
export type GetMarketMetaSuccess = Effect.Effect.Success<GetMarketMeta>;
export type GetMarketMetaError = Effect.Effect.Error<GetMarketMeta>;
export type GetMarketMetaContext = Effect.Effect.Context<GetMarketMeta>;

export function getMarketMeta(staticMarket: StaticMarket) {
  return Effect.gen(function* () {
    const { protocolKey, marketAddress, isDeprecated } = staticMarket;
    const chainKey = getChainKeyFromProtocol(protocolKey);
    const { publicClient } = yield* ChainPublicClientService;

    const siloContract = getContract({
      address: marketAddress,
      abi: siloAbi,
      client: publicClient,
    });

    const [assets, assetStorage] = yield* Effect.tryPromise({
      try: async () => {
        return siloContract.read.getAssetsWithState();
      },
      catch: (cause) => new ContractsClientError({ cause }),
    });

    const assetInfoArray = pipe(
      assets,
      Array.map((asset, i) => {
        const { collateralToken, collateralOnlyToken, debtToken } =
          assetStorage[i];

        const assetInfo: AssetInfo = {
          tokenAddress: asset,
          collateralShareToken: collateralToken,
          protectedShareToken: collateralOnlyToken,
          debtShareToken: debtToken,
        };

        return assetInfo;
      })
    );

    const [baseAsset, firstBridgeAsset, secondBridgeAsset] = yield* Effect.all(
      [
        getMarketAssetMeta({
          marketAddress,
          assetKey: "base",
          assetInfo: assetInfoArray[0],
        }),
        getMarketAssetMeta({
          marketAddress,
          assetKey: "firstBridge",
          assetInfo: assetInfoArray[1],
        }),
        pipe(
          assetInfoArray[2],
          Option.fromNullable,
          Option.match({
            onNone: () => Effect.succeed(undefined),
            onSome: (assetInfo) =>
              getMarketAssetMeta({
                marketAddress,
                assetKey: "secondBridge",
                assetInfo,
              }),
          })
        ),
      ],
      { concurrency: "unbounded" }
    );

    const marketMeta: MarketMeta = {
      protocolKey,
      chainKey,
      marketAddress,
      isDeprecated,

      baseAsset,
      bridgeAssets: secondBridgeAsset
        ? [firstBridgeAsset, secondBridgeAsset]
        : [firstBridgeAsset],
    };

    return marketMeta;
  });
}
